import Badge from '@pelckmans/elementis/components/badge';
import Breadcrumbs from '@pelckmans/elementis/components/breadcrumbs';
import Card from '@pelckmans/elementis/components/card';
import Page from '@pelckmans/elementis/components/page';
import Pill from '@pelckmans/elementis/components/pill';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { MediaContentType } from '../../../api/MediaContentTypeApi';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useRoutes } from '../../../hooks/useRoutes';
import ContentBody from '../../../layout/content/ContentBody';
import ContentHeader from '../../../layout/content/ContentHeader';
import {
  withScrollThreshold,
  WithScrollThresholdProps,
} from '../../../shared-components/elementis/hoc/withScrollThreshold';
import LoadingState from '../../../shared-components/LoadingState';
import SuperModuleBreadcrumb from '../components/breadcrumbs/SuperModuleBreadcrumb';
import EmptyState from './components/EmptyState';
import { CategoricalContentTypeContext } from './context/CategoricalContentTypeContext';
import { flattenCategoricalMaterialPerModule } from './utils/categoricalMaterialUtils';

const BASE = 'PAGES.CATEGORICAL';

const getContentTypeTags = (contentTypes: MediaContentType[]) => {
  return {
    role: undefined,
    'aria-label': undefined,
    children: (
      <>
        {contentTypes.map(ct => (
          <Pill role="listitem" key={ct.id} badge={<Badge>{ct.used}</Badge>}>
            {ct.name}
          </Pill>
        ))}
      </>
    ),
  };
};

interface MaterialBrowserProps extends WithScrollThresholdProps {}

function MaterialOverview({ pastThreshold }: MaterialBrowserProps) {
  const { t } = useTranslation();
  const { categoricalContentPerModule, loading } = useContext(CategoricalContentTypeContext);

  const { buildRelativePath } = useRoutes();
  const categoricalContent = flattenCategoricalMaterialPerModule(categoricalContentPerModule);

  useDocumentTitle(t(`${BASE}.DOCUMENT_TITLE`));

  function renderContentBody() {
    if (loading) {
      return <LoadingState variant="list" aria-label={t(`${BASE}.LOADING.A11Y_LABEL`)} height={80} size={30} />;
    }

    if (categoricalContent.length === 0) {
      return <EmptyState />;
    }

    return (
      <div className="list-cards">
        {categoricalContent.map((cc, index) => (
          <Card
            as="link"
            titleTag="h4"
            titleText={cc.name}
            key={`${cc.name}-${index}`}
            onClick={() => {}}
            tags={getContentTypeTags(cc.contentTypes)}
            href={buildRelativePath('MATERIAL.CATEGORICAL.BROWSE', {
              materialCategory: encodeURIComponent(cc.name),
            })}
          />
        ))}
      </div>
    );
  }

  return (
    <Page>
      <ContentHeader
        cover={
          <Page.Header
            density={pastThreshold ? 'compact' : 'normal'}
            breadcrumbs={
              <Breadcrumbs aria-label={t('MODULE_BREADCRUMB.A11Y_LABEL')}>
                <SuperModuleBreadcrumb />
              </Breadcrumbs>
            }
            headerText={t(`${BASE}.PAGE_TITLE`)}
          />
        }
      />
      <ContentBody>
        <div className="categorical-overview-mode">{renderContentBody()}</div>
      </ContentBody>
    </Page>
  );
}

export default withScrollThreshold(MaterialOverview);
